import moment from 'moment-timezone';
import isArray from 'lodash/isArray';

import getFilterWithCol from '../../helpers/getFilterWithCol';
import clickAnchor from '../../helpers/clickAnchor';

import Types from '../../modules/types';

import tests from '../../helpers/tests';

const getValueForFieldDefault = value => (
  value && value.length
    ? value
    : undefined
);

export const filterSearch = ['FilterField', {
  id: 'search',
  label: null, // 'ID',
  section: 'top',
  prefix: '',
  buildQuery: (value, builder) => {
    if (value?.length) {
      const valueRegex = Types.getSafeSearchRegex(value);
      builder.add(
        'where',
        {
          OR: [
            { 'customerDetails.name': { REGEX: valueRegex, OPTIONS: 'i' } },
            { 'customerDetails.email': { REGEX: valueRegex, OPTIONS: 'i' } },
            {
              'customerDetails.phoneNumberString': {
                REGEX: valueRegex,
                OPTIONS: 'i',
              },
            },
          ],
        },
      );
    }
  },
  getValueForField: value => value || '',
  getValueForQuery: (value) => {
    value = !value
      ? undefined
      : value.target
        ? value.target.value
        : value;
    return !value || !value.length
      ? undefined
      : value;
  },
  field: ['FieldText', {
    name: 'search',
    placeholder: 'Search by name, email or phone number',
  }],
}];

export const filterCity = ['FilterField', {
  id: 'searchCity',
  label: null, // 'ID',
  section: 'top',
  prefix: '',
  buildQuery: (value, builder, filter) => value && builder.add(
    filter.whereSide || 'where',
    {
      OR: [
        { cities: { REGEX: value, OPTIONS: 'i' } },
      ],
    }
  ),
  getValueForField: value => value || '',
  getValueForQuery: (value) => {
    value = !value
      ? undefined
      : value.target
        ? value.target.value
        : value;
    return !value || !value.length
      ? undefined
      : value;
  },
  field: ['FieldText', {
    name: 'search',
    placeholder: 'Search by city',
  }],
}];

export const filterSort = ['FilterField', {
  id: 'sort',
  label: null,
  section: 'top',
  VALUES_MAP: {
    nameDesc: {
      'customerDetails.name': 1,
      _id: 1,
    },
    nameAsc: {
      'customerDetails.name': -1,
      _id: 1,
    },
    lastOrderedAsc: {
      'stats.lastOrderDate': 1,
      'customerDetails.name': 1,
      _id: 1,
    },
    lastOrderedDesc: {
      'stats.lastOrderDate': -1,
      'customerDetails.name': 1,
      _id: 1,
    },
    totalSpentDesc: {
      'stats.orderCostTotal': -1,
      'customerDetails.name': 1,
      _id: 1,
    },
    totalSpentAsc: {
      'stats.orderCostTotal': 1,
      'customerDetails.name': 1,
      _id: 1,
    },
    orderCountDeliveredDesc: {
      'stats.orderCountDelivered': -1,
      'customerDetails.name': 1,
      _id: 1,
    },
    orderCountDeliveredAsc: {
      'stats.orderCountDelivered': 1,
      'customerDetails.name': 1,
      _id: 1,
    },
  },
  buildQueryDefault: 'lastOrderedDesc',
  buildQuery: (value, builder, filter) => (
    filter.VALUES_MAP[value]
    ? builder.add('sort', { ...filter.VALUES_MAP[value] })
    : builder.add('sort', { ...filter.VALUES_MAP[filter.buildQueryDefault] })
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldSelect', {
    name: 'sort',
    placeholder: 'Sort',
    defaultValue: 'lastOrderedDesc',
    initialValue: 'lastOrderedDesc',
    prepareValueForInput: (value, props) => value || props.buildQueryDefault,
    choices: [{
      label: '↓ Last Order Date',
      value: 'lastOrderedDesc',
    }, {
      label: '↑ Last Order Date',
      value: 'lastOrderedAsc',
    }, {
      label: '↓ Name',
      value: 'nameDesc',
    }, {
      label: '↑ Name',
      value: 'nameAsc',
    }, {
      label: '↓ Total Spent',
      value: 'totalSpentDesc',
    }, {
      label: '↑ Total Spent',
      value: 'totalSpentAsc',
    }, {
      label: '↓ Orders Delivered',
      value: 'orderCountDeliveredDesc',
    }, {
      label: '↑ Orders Delivered',
      value: 'orderCountDeliveredAsc',
    }],
  }],
}];

export const [
  filterPurchasedFrom,
  filterPurchasedTo,
] = [
  ['lastOrderFrom', 'Last order after this date', 'GTE'],
  ['lastOrderTo', 'Last order before this date', 'LTE'],
].map(([id, placeholder, operator]) => [
  'FilterField', {
    id,
    label: null,
    section: 'top',
    buildQuery: (value, builder, filter) => value && builder.add(
      filter.whereSide || 'where',
      { 'stats.lastOrderDate': { [operator]: value } },
    ),
    format: ['DD.MM.YYYY.'],
    getValueForField: value => (value ? moment(value) : undefined),
    getValueForQuery: value => (
      value
        ? (
          id === 'from'
            ? moment(value).startOf('day')
            : moment(value).endOf('day')
        ).toJSON()
        : undefined
    ),
    field: ['FieldDatePicker', {
      name: id,
      placeholder,
      showToday: false,
    }],
  },
]);

export const [filterAbove, filterUnder] = [
  ['above', 'Amount more than', 'GTE'],
  ['under', 'Amount less than', 'LTE'],
].map(([id, placeholder, operator]) => [
  'FilterField', {
    id,
    label: null,
    section: 'top',
    buildQuery: (value, builder, filter) => value && builder.add(
      filter.whereSide || 'where',
      { 'stats.orderCostTotal': { [operator]: parseFloat(value, 10) || 0 } },
    ),
    getValueForField: value => value || '',
    getValueForQuery: value => (
      !value
        ? undefined
        : value.target
          ? value.target.value
          : value
    ),
    field: ['FieldText', {
      name: id,
      placeholder,
      addonAfter: Types.CURRENCY,
      // style: { textAlign: 'right' },
      // textAlign: 'right',
      // align: 'right',
    }],
  },
]);

export const filterPagination = ['FilterPagination', {
  id: 'pagination',
  section: 'bottom',
}];

export const filterExport = ['FilterField', {
  id: 'export',
  name: 'export',
  label: null,
  section: 'top',
  buildQuery: () => { },
  propsFromPage: ({ client, queryBuilder, restBaseServer }) => ({
    client,
    queryBuilder,
    restBaseServer,
  }),
  skip: [tests.viewerIsAdminAndDoesNotHavePermission('reportsExport')],
  field: ['Action', {
    name: 'export',
    icon: 'download',
    title: 'Export Customers',
    type: 'default',
    style: { width: '100%' },
    onClick: (props) => clickAnchor({
      url: `${props.restBaseServer}/sale-summaries/export`,
      query: {
        access_token: props.client.getAccessToken(),
        query: props.queryBuilder.compile(),
      },
    }),
  }],
}];

export const filterCountry = ['FilterField', {
  id: 'country',
  label: null,
  section: 'top',
  // eslint-disable-next-line max-len
  buildQuery: (value, builder, filter) => (value && value.length && builder.add(
    filter.whereSide || 'where',
    { countries: { IN: isArray(value) ? value : [value] } },
  )),
  getValueForField: getValueForFieldDefault,
  field: ['FieldSelect', {
    name: 'country',
    placeholder: 'Country',
    mode: 'multiple',
    allowClear: true,
    showArrow: true,
    optionFilterProp: 'children',
    choices: Types.COUNTRIES.map(({ name, iso3a2 }) => ({
      label: name,
      value: iso3a2,
    })),
  }],
}];

export const filterStore = ['FilterField', {
  id: 'store',
  label: null,
  section: 'top',
  propsFromPage: props => ({ client: props.client }),
  buildQuery: (value, builder) => builder.add(
    'where',
    {
      store: {
        IN: (
          value
          ? isArray(value)
            ? value
            : [value]
          : [null]
        ),
      },
    },
  ),
  getValueForField: getValueForFieldDefault,
  field: ['FieldConnectionSelect', {
    name: 'store',
    url: '/stores/list',
    placeholder: 'Stores',
    mode: 'multiple',
    allowClear: true,
    searchPaths: ['name'],
    // getChoiceLabel: choice => `${(choice.data.name)}`,
  }],
}];

export const populateStore = ['FilterHidden', {
  id: 'populate',
  build: builder => builder.add(
    'populate',
    {
      store: { name: true },
      customer: { stores: { name: true } },
    },
  ),
}];

export const ADMIN = [
  ...[
    [filterSearch, 12],
    [filterCity, 12],

    [filterSort, 8],
    [filterStore, 8],
    [filterCountry, 8],

    [filterAbove, 12],
    [filterUnder, 12],

    [filterPurchasedFrom, 12],
    [filterPurchasedTo, 12],

    [filterExport, 24],
  ].map((config) => getFilterWithCol(...config)),

  populateStore,
  filterPagination,
];

export const SELLER = [
  ...[
    [filterSearch, 12],
    [filterCity, 12],

    [filterSort, 12],

    [filterCountry, 12],

    [filterAbove, 12],
    [filterUnder, 12],

    [filterPurchasedFrom, 12],
    [filterPurchasedTo, 12],

    [filterExport, 24],
  ].map((config) => getFilterWithCol(...config)),

  // populateStore,
  filterPagination,
];
